import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/hooks/useAuthStore'
import { FavoriteAPI } from '@certi/shared/api/user/FavoriteAPI'
import { ListingsAPI } from '@certi/shared/api/listings/ListingsAPI'
import { FavoriteType } from '@certi/shared/types/types.user'
import type { FavoriteResponse } from '@certi/shared/types/types.user'
import type { ListingResponse } from '@certi/shared/types/types.marketplace'
import ListingCard from '@/components/marketplace/ListingCard'

export default function FavoritesPage() {
  const navigate = useNavigate()
  const { user, accessToken } = useAuthStore()
  const [activeTab, setActiveTab] = useState('products')
  const [favorites, setFavorites] = useState<FavoriteResponse[]>([])
  const [listings, setListings] = useState<ListingResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const tabs = [
    { id: 'products', label: 'Products' },
    { id: 'categories', label: 'Categories' },
    { id: 'sellers', label: 'Sellers' }
  ]

  const isAuthenticated = !!user && !!accessToken

  useEffect(() => {
    if (isAuthenticated && user && accessToken) {
      loadFavorites()
    } else {
      setLoading(false)
    }
  }, [isAuthenticated, user, accessToken])

  const loadFavorites = async () => {
    if (!user || !accessToken) return

    try {
      setLoading(true)
      setError(null)

      // Fetch all favorites
      const allFavorites = await FavoriteAPI.getUserFavorites(user.id, accessToken)
      setFavorites(allFavorites)

      // Fetch listing details for favorited listings
      const listingFavorites = allFavorites.filter(f => f.targetType === FavoriteType.LISTING)
      const listingPromises = listingFavorites.map(f =>
        ListingsAPI.getListingById(f.targetId).catch(() => null)
      )
      const fetchedListings = await Promise.all(listingPromises)
      setListings(fetchedListings.filter((l): l is ListingResponse => l !== null))
    } catch (err) {
      console.error('Failed to load favorites:', err)
      setError('Failed to load your favorites. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleFavorite = async (listingId: string) => {
    if (!user || !accessToken) return

    try {
      await FavoriteAPI.toggleFavorite(
        user.id,
        { targetType: FavoriteType.LISTING, targetId: listingId },
        accessToken
      )

      // Reload favorites
      await loadFavorites()
    } catch (err) {
      console.error('Failed to toggle favorite:', err)
    }
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold gradient-text">My Favourites</h1>
        </div>

        <div className="flex items-center justify-center min-h-[400px]">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <svg className="w-24 h-24 mx-auto text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </div>

            <div className="mb-8 space-y-4">
              <p className="text-lg leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                Enjoy a more personalized experience by following your favorite teams.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Tap the heart icon to get notifications about the products and moments you care about most.
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 text-white"
              style={{
                backgroundColor: 'var(--brand-primary)',
                boxShadow: '0 2px 8px rgba(11, 77, 42, 0.3)'
              }}
            >
              Login or Create Account
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show favorites content when authenticated
  const productFavorites = favorites.filter(f => f.targetType === FavoriteType.LISTING)
  const categoryFavorites = favorites.filter(f => f.targetType === FavoriteType.CATEGORY)
  const sellerFavorites = favorites.filter(f => f.targetType === FavoriteType.SELLER)

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold gradient-text">My Favourites</h1>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-1 mb-8 border-b" style={{ borderColor: 'var(--border-primary)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 font-medium text-sm rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent hover:border-gray-300'
            }`}
            style={{
              color: activeTab === tab.id ? 'var(--brand-primary)' : 'var(--text-secondary)',
              borderBottomColor: activeTab === tab.id ? 'var(--brand-primary)' : 'transparent'
            }}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="ml-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                ({tab.id === 'products' ? productFavorites.length :
                  tab.id === 'categories' ? categoryFavorites.length :
                  sellerFavorites.length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                 style={{ borderColor: 'var(--brand-primary)' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Loading your favorites...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadFavorites}
              className="px-6 py-2 rounded-lg font-medium"
              style={{
                backgroundColor: 'var(--brand-primary)',
                color: 'white'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Products Tab */}
          {activeTab === 'products' && (
            <div>
              {listings.length === 0 ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <div className="text-center max-w-md">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    <p className="text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                      No favorite products yet
                    </p>
                    <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                      Browse the marketplace and tap the heart icon to save products you love
                    </p>
                    <button
                      onClick={() => navigate('/marketplace')}
                      className="px-6 py-2 rounded-lg font-medium"
                      style={{
                        backgroundColor: 'var(--brand-primary)',
                        color: 'white'
                      }}
                    >
                      Browse Marketplace
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {listings.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      isFavorite={true}
                      onPress={() => navigate(`/marketplace/${listing.slug || listing.id}`)}
                      onToggleFavorite={() => handleToggleFavorite(listing.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Categories Tab */}
          {activeTab === 'categories' && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center max-w-md">
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  Category favorites coming soon
                </p>
              </div>
            </div>
          )}

          {/* Sellers Tab */}
          {activeTab === 'sellers' && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center max-w-md">
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  Seller favorites coming soon
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
